import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { CloudArrowUp, CircleNotch, CheckCircle, Warning, ArrowClockwise, ArrowFatDown, ArrowFatUp } from "@phosphor-icons/react";

/**
 * Read-Back verifier panel — the §L5 loop from the Drake AI design doc.
 *
 * A firm user picks a packet, uploads the Drake-exported return PDF, and the
 * LLM verifier reports match / write-discrepancy per canonical field.
 */
export default function ReadBackPanel({ engagementId, packets = [] }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [selectedPacket, setSelectedPacket] = useState(packets[0]?.id || "");
  const [file, setFile] = useState(null);

  // Refresh when packets prop changes and current selection is gone
  const currentPacket = packets.find((p) => p.id === selectedPacket) || packets[0];
  if (currentPacket && currentPacket.id !== selectedPacket) {
    // sync once
    setTimeout(() => setSelectedPacket(currentPacket.id), 0);
  }

  const { data: verifications } = useQuery({
    queryKey: ["drake-verifications", engagementId],
    queryFn: () => api.get(`/drake/verifications?engagement_id=${engagementId}`).then((r) => r.data),
    enabled: !!engagementId,
  });

  const verify = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Pick a Drake-exported PDF first.");
      if (!selectedPacket) throw new Error("Select a packet.");
      const fd = new FormData();
      fd.append("drake_export", file);
      return api.post(`/drake/packet/${selectedPacket}/verify`, fd);
    },
    onSuccess: (r) => {
      const d = r.data;
      toast.success(`${d.match_pct}% match · ${d.matches} verified · ${d.mismatches} discrepancies`);
      qc.invalidateQueries({ queryKey: ["drake-verifications", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-discrepancies", engagementId] });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => toast.error(errText(e)),
  });

  const { data: discrepancies } = useQuery({
    queryKey: ["drake-discrepancies", engagementId],
    queryFn: () => api.get(`/drake/discrepancies?engagement_id=${engagementId}`).then((r) => r.data),
    enabled: !!engagementId,
  });

  const acceptAll = useMutation({
    mutationFn: () => api.post(`/drake/reconcile/${engagementId}/accept-drake-all`),
    onSuccess: (r) => {
      toast.success(`Accepted Drake values on ${r.data.accepted} fields${r.data.skipped ? ` (${r.data.skipped} skipped — no reported value)` : ""}`);
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-discrepancies", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const acceptOne = useMutation({
    mutationFn: (fieldId) => api.post(`/drake/reconcile/field/${fieldId}/accept-drake`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-discrepancies", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const keepOurs = useMutation({
    mutationFn: (fieldId) => api.post(`/drake/reconcile/field/${fieldId}/keep-ours`),
    onSuccess: () => {
      toast.success("Kept firm value — flagged for re-export");
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-discrepancies", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const latest = (verifications || [])[0];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="read-back-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <ArrowClockwise size={13} weight="fill" className="text-indigo-600" />
          Read-back verification
        </h3>
        {latest && (
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border"
            style={{
              background: latest.match_pct >= 95 ? "#ecfdf5" : latest.match_pct >= 80 ? "#fffbeb" : "#fef2f2",
              color: latest.match_pct >= 95 ? "#065f46" : latest.match_pct >= 80 ? "#92400e" : "#991b1b",
              borderColor: latest.match_pct >= 95 ? "#a7f3d0" : latest.match_pct >= 80 ? "#fde68a" : "#fecaca",
            }}
            data-testid="rb-latest-pct"
          >
            {latest.match_pct}% match
          </span>
        )}
      </div>
      <p className="text-[10.5px] text-zinc-500 leading-snug mb-3">
        Upload the return PDF Drake produced. The verifier reads each MeF field back and flags any write discrepancy per §L5.
      </p>

      {packets.length === 0 ? (
        <p className="text-[11px] text-zinc-400 italic" data-testid="rb-empty">Export a GruntWorx packet first.</p>
      ) : (
        <>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Packet</label>
          <select
            data-testid="rb-packet-select"
            value={selectedPacket}
            onChange={(e) => setSelectedPacket(e.target.value)}
            className="w-full text-xs border border-zinc-200 rounded-md px-2 py-1.5 mb-2 bg-white"
          >
            {packets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id.slice(0, 8)} · {new Date(p.generated_at).toLocaleDateString()}
              </option>
            ))}
          </select>

          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Drake export PDF</label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-[11px] mb-2 file:mr-2 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:px-2 file:py-1 file:cursor-pointer"
            data-testid="rb-file-input"
          />

          <button
            data-testid="rb-verify-btn"
            disabled={!file || verify.isPending}
            onClick={() => verify.mutate()}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-1.5 transition-colors duration-150"
          >
            {verify.isPending ? (
              <><CircleNotch size={12} className="animate-spin" /> Verifying…</>
            ) : (
              <><CloudArrowUp size={12} /> Run read-back</>
            )}
          </button>
        </>
      )}

      {latest && (
        <div className="mt-3 pt-3 border-t border-zinc-100" data-testid="rb-latest-block">
          <div className="flex items-center gap-2 text-[10.5px] text-zinc-600">
            <CheckCircle size={12} weight="fill" className="text-emerald-600" />
            <span>{latest.matches} verified</span>
            {latest.mismatches > 0 && (
              <>
                <span className="text-zinc-300">·</span>
                <Warning size={12} weight="fill" className="text-rose-600" />
                <span>{latest.mismatches} discrepancies</span>
              </>
            )}
          </div>
          <p className="text-[10px] text-zinc-400 mt-0.5">
            {new Date(latest.verified_at).toLocaleString()} · by {latest.verified_by}
          </p>
        </div>
      )}

      {(discrepancies || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2" data-testid="rb-reconcile-block">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-slate-900 flex items-center gap-1">
              <Warning size={12} weight="fill" className="text-rose-600" />
              {discrepancies.length} to reconcile
            </p>
            <button
              data-testid="rb-accept-all-btn"
              disabled={acceptAll.isPending}
              onClick={() => acceptAll.mutate()}
              className="text-[10.5px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-2 py-1 transition-colors duration-150 disabled:opacity-50"
              title="One-click: overwrite our value with what Drake actually reads, for every discrepancy with a reported value"
            >
              {acceptAll.isPending ? "Accepting…" : "Accept all Drake values"}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {discrepancies.map((f) => (
              <div key={f.id} className="rounded-sm border border-rose-200 bg-rose-50/70 px-2 py-1.5" data-testid={`rb-recon-row-${f.id}`}>
                <p className="font-mono text-[10.5px] text-slate-800 truncate" title={f.mef_path}>{f.mef_path}</p>
                <p className="text-[10.5px] text-zinc-700 mt-0.5">
                  <span className="text-emerald-700">ours: {String(f.value ?? "—")}</span>
                  <span className="text-zinc-300"> · </span>
                  <span className="text-rose-700">
                    Drake reads: {f.verified_value === null || f.verified_value === undefined ? <em>missing on return</em> : String(f.verified_value)}
                  </span>
                </p>
                <div className="flex items-center gap-1 mt-1.5">
                  {f.verified_value !== null && f.verified_value !== undefined && (
                    <button
                      data-testid={`rb-accept-one-${f.id}`}
                      disabled={acceptOne.isPending}
                      onClick={() => acceptOne.mutate(f.id)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm px-1.5 py-0.5 transition-colors duration-150 disabled:opacity-50"
                    >
                      <ArrowFatDown size={9} weight="fill" /> Accept Drake
                    </button>
                  )}
                  <button
                    data-testid={`rb-keep-ours-${f.id}`}
                    disabled={keepOurs.isPending}
                    onClick={() => keepOurs.mutate(f.id)}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-800 bg-white border border-zinc-300 hover:bg-zinc-50 rounded-sm px-1.5 py-0.5 transition-colors duration-150 disabled:opacity-50"
                  >
                    <ArrowFatUp size={9} weight="fill" /> Keep ours
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
